import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button as MuiButton,
  Chip,
  IconButton,
  MenuItem,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { apiClient } from '../../apiClient';
import { API_ENDPOINTS, ROUTES } from '../../constants';
import { Order } from '../../types';
import { formatCurrency } from '../../utils/pricing';

interface OrdersResponse {
  orders: Order[];
  currentPage: number;
  totalPages: number;
  totalOrders: number;
}

const AdminOrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<OrdersResponse>(API_ENDPOINTS.ORDERS, {
        params: {
          page,
          limit: 20,
          status: statusFilter || undefined,
          paymentStatus: paymentFilter || undefined
        }
      });
      setOrders(data.orders || []);
      setTotalPages(data.totalPages || 1);
    } finally {
      setLoading(false);
    }
  }, [page, paymentFilter, statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const exportCsv = async () => {
    const response = await apiClient.get(`${API_ENDPOINTS.ORDERS}/export/csv`, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(response.data as Blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `vediluxe-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    setPage(1);
  }, [statusFilter, paymentFilter]);

  const visibleOrders = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter(
      (o) =>
        o.customerName.toLowerCase().includes(q) ||
        o.customerEmail.toLowerCase().includes(q) ||
        o._id.toLowerCase().includes(q)
    );
  }, [orders, query]);

  const updateStatus = async (id: string, status: Order['status']) => {
    await apiClient.patch(`${API_ENDPOINTS.ORDERS}/${id}/status`, { status });
    fetchOrders();
  };

  const updatePayment = async (id: string, paymentStatus: Order['paymentStatus']) => {
    await apiClient.patch(`${API_ENDPOINTS.ORDERS}/${id}/payment`, { paymentStatus });
    fetchOrders();
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Orders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fulfillment-focused workspace for processing, payment updates, and order visibility.
          </Typography>
        </Box>
        <MuiButton variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportCsv}>
          Export CSV
        </MuiButton>
      </Box>

      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 3,
          bgcolor: '#f9fafb',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 200px 200px' },
          gap: 1.5
        }}
      >
        <TextField
          size="small"
          placeholder="Search by order id, name, email"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <TextField
          select
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <MenuItem value="">All statuses</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="processing">Processing</MenuItem>
          <MenuItem value="shipped">Shipped</MenuItem>
          <MenuItem value="delivered">Delivered</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          value={paymentFilter}
          onChange={(e) => setPaymentFilter(e.target.value)}
        >
          <MenuItem value="">All payment states</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="paid">Paid</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
        </TextField>
      </Box>

      {loading ? (
        <Box sx={{ display: 'grid', gap: 1 }}>
          <Skeleton variant="rounded" height={46} />
          <Skeleton variant="rounded" height={46} />
          <Skeleton variant="rounded" height={46} />
          <Skeleton variant="rounded" height={46} />
        </Box>
      ) : (
        <>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleOrders.map((order) => (
              <TableRow key={order._id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="Open order">
                      <IconButton
                        size="small"
                        component={RouterLink}
                        to={`${ROUTES.ADMIN}/orders/${order._id}`}
                      >
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    #{order._id.slice(-6).toUpperCase()}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{order.customerName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {order.customerEmail}
                  </Typography>
                </TableCell>
                <TableCell>{order.items.length}</TableCell>
                <TableCell>{formatCurrency(order.totalAmount)}</TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    value={order.status}
                    onChange={(e) => updateStatus(order._id, e.target.value as Order['status'])}
                    sx={{ minWidth: 130 }}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="processing">Processing</MenuItem>
                    <MenuItem value="shipped">Shipped</MenuItem>
                    <MenuItem value="delivered">Delivered</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </TextField>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      size="small"
                      label={order.paymentStatus}
                      color={order.paymentStatus === 'paid' ? 'success' : 'default'}
                    />
                    <TextField
                      select
                      size="small"
                      value={order.paymentStatus}
                      onChange={(e) =>
                        updatePayment(order._id, e.target.value as Order['paymentStatus'])
                      }
                      sx={{ minWidth: 100 }}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                    </TextField>
                  </Box>
                </TableCell>
                <TableCell>
                  {order.updatedAt ? new Date(order.updatedAt).toLocaleDateString() : '-'}
                </TableCell>
              </TableRow>
            ))}
            {visibleOrders.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No orders match current filters.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              page={page}
              count={totalPages}
              color="primary"
              onChange={(_, nextPage) => setPage(nextPage)}
            />
          </Box>
        )}
        </>
      )}
    </Box>
  );
};

export default AdminOrdersPage;
